import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SelfieCapture from './SelfieCapture';
import CountryCodePicker from './CountryCodePicker';
import countryCodes from './CountryCodes';
import { validateEmail, validateMobileNumber, validateName } from '../../utils/helpers';
import { registerUser } from '../../services/api';

const CONTACT_METHOD = {
    MOBILE: 'mobile',
    EMAIL: 'email'
};

const EVENT_TYPE_FIELDS = {
    wedding: ['groom_name', 'bride_name'],
    engagement: ['groom_name', 'bride_name'],
    'couple shoot': ['partner_one', 'partner_two'],
    birthday: ['celebrant_name'],
    'family photoshoot': ['family_name'],
    'maternity photoshoot': ['parent_name'],
    'new born baby photoshoot': ['baby_name'],
    corporate: ['company_name', 'event_title'],
    'product photoshoot': ['product_name', 'company_name'],
    'fashion model photoshoot': ['model_name', 'theme'],
    'real-estate photoshoot': ['property_name', 'company_name'],
    'restaurant photoshoot': ['restaurant_name'],
    'theme photoshoot': ['theme_name', 'client_name'],
    others: ['event_name']
};

const COUPLE_EVENT_TYPES = new Set(['wedding', 'engagement', 'couple shoot']);

const asNonEmptyString = (value) => (typeof value === 'string' ? value.trim() : '');

const getEventNameParts = (eventDetails) => {
    const names = eventDetails?.names && typeof eventDetails.names === 'object' ? eventDetails.names : {};
    const eventTypeName = asNonEmptyString(eventDetails?.event_type_name);
    const configuredFields = EVENT_TYPE_FIELDS[eventTypeName.toLowerCase()] || [];

    let values = configuredFields
        .map((field) => asNonEmptyString(names[field]))
        .filter(Boolean);

    if (!values.length) {
        values = Object.values(names)
            .map((value) => asNonEmptyString(value))
            .filter(Boolean);
    }

    if (!values.length) {
        values = [eventDetails?.groom_name, eventDetails?.bride_name]
            .map((value) => asNonEmptyString(value))
            .filter(Boolean);
    }

    return values;
};

const getLocaleRegionCode = () => {
    const localeCandidates = [navigator.language, ...(navigator.languages || [])].filter(Boolean);

    for (const locale of localeCandidates) {
        const match = locale.match(/[-_]([a-z]{2})$/i);
        if (match?.[1]) {
            return match[1].toUpperCase();
        }
    }

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    if (timezone.includes('Dubai') || timezone.includes('Abu_Dhabi')) {
        return 'AE';
    }

    return 'IN';
};

const getDefaultCountryCode = () => (getLocaleRegionCode() === 'AE' ? '+971' : '+91');

const RegistrationForm = ({ uuid, eventDetails }) => {
    const navigate = useNavigate();
    const [name, setName] = useState('');
    const [mobileNumber, setMobileNumber] = useState('');
    const [email, setEmail] = useState('');
    const [contactMethod, setContactMethod] = useState(CONTACT_METHOD.MOBILE);
    const [countryCode, setCountryCode] = useState('+91');
    const [agreeTerms, setAgreeTerms] = useState(false);
    const [showCamera, setShowCamera] = useState(false);
    const [selfieImage, setSelfieImage] = useState(null);
    const [selfiePreview, setSelfiePreview] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        const detectedCode = getDefaultCountryCode();
        const isSupportedCode = countryCodes.some((item) => item.code === detectedCode);
        if (isSupportedCode) {
            setCountryCode(detectedCode);
        }
    }, []);

    const isFormValid = () => {
        if (!validateName(name)) {
            setError('Please enter your name (at least 2 characters).');
            return false;
        }

        if (contactMethod === CONTACT_METHOD.MOBILE) {
            if (!validateMobileNumber(mobileNumber)) {
                setError('Please enter a valid mobile number.');
                return false;
            }
        } else if (!validateEmail(email)) {
            setError('Please enter a valid email address.');
            return false;
        }

        if (!agreeTerms) {
            setError('Please agree to the terms and conditions.');
            return false;
        }

        setError('');
        return true;
    };

    const handleOpenCamera = () => {
        if (!isFormValid()) return;
        setShowCamera(true);
    };

    const handleSelfieCapture = (file, preview) => {
        setSelfieImage(file);
        setSelfiePreview(preview);
        setShowCamera(false);
    };

    const handleCloseCamera = () => {
        setShowCamera(false);
    };

    const handleContactMethodChange = (method) => {
        if (method === contactMethod) return;

        setContactMethod(method);
        setError('');

        if (method === CONTACT_METHOD.MOBILE) {
            setEmail('');
            return;
        }

        setMobileNumber('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isFormValid()) return;

        if (!selfieImage) {
            setError('Please take a selfie to continue.');
            return;
        }

        setLoading(true);
        setError('');

        const normalizedName = name.trim();
        const normalizedMobile = mobileNumber.trim();
        const normalizedEmail = email.trim();

        try {
            const response = await registerUser(uuid, {
                name: normalizedName,
                mobileNumber: contactMethod === CONTACT_METHOD.MOBILE ? normalizedMobile : '',
                email: contactMethod === CONTACT_METHOD.EMAIL ? normalizedEmail : '',
                countryCode: contactMethod === CONTACT_METHOD.MOBILE ? countryCode : '',
                selfieImage
            });

            if (response.result) {
                navigate(`/event/${uuid}/registration-result/success`, {
                    state: {
                        name: normalizedName,
                        mobileNumber: contactMethod === CONTACT_METHOD.MOBILE ? normalizedMobile : '',
                        email: contactMethod === CONTACT_METHOD.EMAIL ? normalizedEmail : '',
                        countryCode: contactMethod === CONTACT_METHOD.MOBILE ? countryCode : '',
                        securePin: response.datsecure_pin,
                        selfiePreview
                    }
                });
            } else {
                setError(response.message || 'Registration failed. Please try again.');
                setLoading(false);
            }
        } catch (err) {
            setError('Registration failed. Please try again later.');
            console.error('Registration error:', err);
            setLoading(false);
        }
    };

    const eventTypeName = asNonEmptyString(eventDetails?.event_type_name);
    const eventNameParts = getEventNameParts(eventDetails);
    const isCoupleEvent = COUPLE_EVENT_TYPES.has(eventTypeName.toLowerCase()) && eventNameParts.length === 2;

    return (
        <div className="registration-container">
            {showCamera ? (
                <SelfieCapture
                    onCapture={handleSelfieCapture}
                    onClose={handleCloseCamera}
                />
            ) : (
                <form onSubmit={handleSubmit} className="registration-form">
                    <div className="form-header">
                        
                        {eventNameParts.length > 0 && (
                            <h2 className={`event-names ${isCoupleEvent ? 'is-couple' : ''}`}>
                                {eventNameParts.map((value, index) => (
                                    <React.Fragment key={`${value}-${index}`}>
                                        {index > 0 && (
                                            <span className="name-divider">
                                                {isCoupleEvent ? '&' : '\u2022'}
                                            </span>
                                        )}
                                        <span>{value}</span>
                                    </React.Fragment>
                                ))}
                            </h2>
                        )}
                        {eventTypeName && <p className="event-type-label">{eventTypeName}</p>}
                        <h3 className="form-title">Get your photos</h3>
                        <p className="form-subtitle">
                            Share your selfie and choose WhatsApp or Email for secure photo delivery.
                        </p>
                    </div>

                    {error && (
                        <div className="error-message">
                            <p>{error}</p>
                        </div>
                    )}

                    <div className="input-group">
                        <div className="input-icon">
                            <i className="fa fa-user"></i>
                        </div>
                        <input
                            type="text"
                            className="input input-with-icon"
                            placeholder="Your Name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                        />
                    </div>

                    <div className="contact-method-section">
                        <p className="contact-method-title">Where should your photo magic arrive?</p>
                        <div className="contact-method-toggle" role="tablist" aria-label="Contact method">
                            <button
                                type="button"
                                className={`contact-method-button ${contactMethod === CONTACT_METHOD.MOBILE ? 'active' : ''}`}
                                onClick={() => handleContactMethodChange(CONTACT_METHOD.MOBILE)}
                            >
                                <i className="fa fa-whatsapp"></i>
                                WhatsApp
                            </button>
                            <button
                                type="button"
                                className={`contact-method-button ${contactMethod === CONTACT_METHOD.EMAIL ? 'active' : ''}`}
                                onClick={() => handleContactMethodChange(CONTACT_METHOD.EMAIL)}
                            >
                                <i className="fa fa-envelope"></i>
                                Email
                            </button>
                        </div>
                    </div>

                    {contactMethod === CONTACT_METHOD.MOBILE ? (
                        <div className="phone-input-group">
                            <CountryCodePicker
                                selectedCode={countryCode}
                                onSelect={setCountryCode}
                            />
                            <div className="phone-input-field">
                                <div className="input-icon">
                                    <i className="fa fa-whatsapp"></i>
                                </div>
                                <input
                                    type="tel"
                                    className="input input-with-icon phone-input"
                                    placeholder="Your WhatsApp Number"
                                    value={mobileNumber}
                                    onChange={(e) => setMobileNumber(e.target.value)}
                                    required={contactMethod === CONTACT_METHOD.MOBILE}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="input-group">
                            <div className="input-icon">
                                <i className="fa fa-envelope"></i>
                            </div>
                            <input
                                type="email"
                                className="input input-with-icon"
                                placeholder="Your Email Address"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required={contactMethod === CONTACT_METHOD.EMAIL}
                            />
                        </div>
                    )}

                    <div className="checkbox-group">
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                checked={agreeTerms}
                                onChange={(e) => setAgreeTerms(e.target.checked)}
                            />
                            <span>
                                I agree to the <a href="#">Terms of Service</a>. Learn about how we use and protect your data in our <a
                                href="#">Privacy Policy</a>.
                            </span>
                        </label>
                    </div>

                    {selfiePreview ? (
                        <div className="selfie-preview">
                            <h3>Your Selfie</h3>
                            <div className="avatar">
                                <img src={selfiePreview} alt="Your selfie" />
                            </div>
                            <div className="center">
                                <button type="button" className="button button-outlined button-icon" onClick={handleOpenCamera}>
                                    <i className="fa fa-camera"></i>
                                    Retake Selfie
                                </button>
                            </div>
                        </div>
                    ) : (
                        <button
                            type="button"
                            className="button button-icon selfie-button"
                            onClick={handleOpenCamera}
                        >
                            <i className="fa fa-camera"></i>
                            Upload Selfie
                        </button>
                    )}

                    <button
                        type="submit"
                        className="button button-primary button-icon"
                        disabled={loading || !selfieImage}
                    >
                        {loading ? (
                            <>
                                <i className="fa fa-spinner fa-spin"></i>
                                Processing...
                            </>
                        ) : (
                            <>
                                <i className="fa fa-check"></i>
                                Register
                            </>
                        )}
                    </button>

                    <div className="info-text">
                        <small>
                            By clicking register, you give us permission to use AI facial recognition to securely
                            deliver event photos directly to you.
                        </small>
                    </div>
                </form>
            )}
        </div>
    );
};

export default RegistrationForm;
